import React, { memo, useEffect, useRef, useState } from "react";
import {
  STEP_META,
  MIN_DELAY_HOURS,
  MAX_DELAY_DAYS,
  branchMeta,
  formatDelayAmount,
  formatStepCanvasLabel,
  normalizeDelayForApi,
  resolveDelayMs,
} from "./sequenceUtils";
import {
  AlertTriangleIcon,
  CheckIcon,
  CircleCheckIcon,
  ClockIcon,
  CornerDownRightIcon,
  MoreVerticalIcon,
  PencilIcon,
  PlusIcon,
  SendIcon,
  StepTypeIcon,
  TrashIcon,
  XIcon,
} from "./SequenceIcons";

const LINE = "border-gray-300";

/** Close a popover when clicking anywhere outside `ref`. */
function useOutsideClose(ref, open, onClose) {
  useEffect(() => {
    if (!open) return undefined;
    const handler = (event) => {
      if (ref.current && !ref.current.contains(event.target)) onClose();
    };
    document.addEventListener("pointerdown", handler);
    return () => document.removeEventListener("pointerdown", handler);
  }, [ref, open, onClose]);
}

export function VLine({ height = 24 }) {
  return <div className="w-px bg-gray-300 shrink-0" style={{ height }} />;
}

export function StartNode() {
  return (
    <div className="flex items-center gap-2 text-sm font-medium text-gray-800 py-1">
      <SendIcon size={16} className="text-gray-600" />
      Sequence start
    </div>
  );
}

export function EndNode({ onRemove }) {
  return (
    <div className="group relative flex items-center gap-2 text-sm font-medium text-gray-800 py-1">
      <CircleCheckIcon size={16} className="text-gray-600" />
      Sequence end
      {onRemove ? (
        <button
          type="button"
          data-nopan
          onClick={onRemove}
          title="Remove end and continue this path"
          className="absolute -right-7 w-5 h-5 rounded-full border border-gray-200 bg-white text-gray-400 hover:text-red-500 hover:border-red-200 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <XIcon size={12} />
        </button>
      ) : null}
    </div>
  );
}

export function AddButton({ onClick }) {
  return (
    <button
      type="button"
      data-nopan
      onClick={onClick}
      title="Add a step here"
      className="w-7 h-7 rounded-md border border-gray-300 bg-white text-gray-500 shadow-sm flex items-center justify-center hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
    >
      <PlusIcon size={16} />
    </button>
  );
}

/** Unfinished path: add a step or end the sequence here. */
export function OpenSlot({ onAdd, onEnd }) {
  return (
    <div
      data-nopan
      className="flex items-center gap-1.5 p-1.5 rounded-lg border border-gray-200 bg-white shadow-[0_2px_8px_rgba(15,23,42,0.08)]"
    >
      <button
        type="button"
        onClick={onAdd}
        title="Add a step"
        className="w-8 h-8 rounded-md border border-gray-200 text-gray-500 flex items-center justify-center hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
      >
        <PlusIcon size={18} />
      </button>
      <button
        type="button"
        onClick={onEnd}
        title="End sequence here"
        className="w-8 h-8 rounded-md border border-gray-200 text-gray-400 flex items-center justify-center hover:border-emerald-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors"
      >
        <CheckIcon size={16} />
      </button>
    </div>
  );
}

/** Path that rejoins a step already drawn elsewhere in the tree. */
export function JumpNode({ label }) {
  return (
    <div className="flex items-center gap-1.5 text-xs text-gray-600 border border-dashed border-gray-300 rounded-lg bg-white px-2.5 py-1.5">
      <CornerDownRightIcon size={14} className="text-gray-400" />
      Continues to <span className="font-medium text-gray-800">{label}</span>
    </div>
  );
}

function BranchPill({ condition }) {
  const meta = branchMeta(condition);
  return (
    <span
      className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-medium ${
        meta.positive
          ? "bg-emerald-50 border-emerald-200 text-emerald-700"
          : "bg-red-50 border-red-200 text-red-500"
      }`}
    >
      {meta.positive ? <CheckIcon size={11} /> : <XIcon size={11} />}
      {meta.label}
    </span>
  );
}

/**
 * Splits the flow into one column per branch. Columns share one width so the
 * parent line always lands on the junction in the middle.
 */
export function BranchSplit({ items }) {
  const n = items.length;
  return (
    <div className="relative grid" style={{ gridTemplateColumns: `repeat(${n}, 1fr)` }}>
      {n === 2 ? (
        <>
          <div className={`absolute top-0 right-1/2 w-3 h-3 border-r border-b ${LINE} rounded-br-xl`} />
          <div className={`absolute top-0 left-1/2 w-3 h-3 border-l border-b ${LINE} rounded-bl-xl`} />
        </>
      ) : (
        <div className="absolute top-0 left-1/2 h-3 w-px bg-gray-300" />
      )}
      {items.map((item, i) => {
        const first = i === 0;
        const last = i === n - 1;
        let connector;
        let pillLeft = "50%";
        if (n === 1) {
          connector = <div className="absolute top-0 bottom-0 left-1/2 w-px bg-gray-300" />;
        } else if (n === 2 && first) {
          connector = (
            <div className={`absolute top-[11px] bottom-0 left-1/2 right-3 border-t border-l ${LINE} rounded-tl-xl`} />
          );
          pillLeft = "calc(75% - 6px)";
        } else if (n === 2 && last) {
          connector = (
            <div className={`absolute top-[11px] bottom-0 left-3 right-1/2 border-t border-r ${LINE} rounded-tr-xl`} />
          );
          pillLeft = "calc(25% + 6px)";
        } else {
          connector = (
            <>
              <div
                className={`absolute top-[11px] h-px bg-gray-300 ${
                  first ? "left-1/2 right-0" : last ? "left-0 right-1/2" : "left-0 right-0"
                }`}
              />
              <div className="absolute top-[11px] bottom-0 left-1/2 w-px bg-gray-300" />
            </>
          );
        }
        return (
          <div key={item.key} className="flex flex-col items-center min-w-[300px]">
            <div className="relative self-stretch h-11 shrink-0">
              {connector}
              <div
                className="absolute top-[11px] -translate-x-1/2 -translate-y-1/2 z-[1]"
                style={{ left: pillLeft }}
              >
                <BranchPill condition={item.condition} />
              </div>
            </div>
            <div className="flex flex-col items-center px-6">{item.content}</div>
          </div>
        );
      })}
    </div>
  );
}

const DELAY_UNITS = ["hours", "days", "weeks"];

function DelayEditor({ step, onSave, onClose }) {
  const initial = normalizeDelayForApi(step.delay, step.delayUnit);
  const [amount, setAmount] = useState(String(initial.delay));
  const [unit, setUnit] = useState(initial.delayUnit);

  const value = Number(amount) || 0;
  const ms = resolveDelayMs(value, unit);
  const invalid = value < 0
    || (ms > 0 && (ms < MIN_DELAY_HOURS * 3600000 || ms > MAX_DELAY_DAYS * 86400000));

  const submit = (event) => {
    event.preventDefault();
    if (invalid) return;
    onSave(value > 0 ? { delay: value, delayUnit: unit } : { delay: 0, delayUnit: "hours" });
  };

  return (
    <form
      onSubmit={submit}
      className="absolute left-0 right-0 top-full mt-2 z-30 rounded-xl border border-gray-200 bg-white p-3 shadow-lg text-left select-text"
    >
      <div className="text-xs font-medium text-gray-700 mb-2">Wait before this step</div>
      <div className="flex gap-2">
        <input
          type="number"
          min={0}
          autoFocus
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className="w-20 border border-gray-200 rounded-lg px-2 py-1.5 text-sm"
        />
        <select
          value={unit}
          onChange={(e) => setUnit(e.target.value)}
          className="flex-1 border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white"
        >
          {DELAY_UNITS.map((u) => (
            <option key={u} value={u}>
              {u[0].toUpperCase() + u.slice(1)}
            </option>
          ))}
        </select>
      </div>
      <div className={`mt-1.5 text-[11px] ${invalid ? "text-red-500" : "text-gray-400"}`}>
        0 = no delay. Otherwise between {MIN_DELAY_HOURS} hours and {MAX_DELAY_DAYS} days.
      </div>
      <div className="mt-3 flex justify-end gap-2">
        <button type="button" onClick={onClose} className="px-3 py-1 rounded-lg border text-xs">
          Cancel
        </button>
        <button
          type="submit"
          disabled={invalid}
          className="px-3 py-1 rounded-lg bg-blue-500 text-white text-xs disabled:opacity-50"
        >
          Save
        </button>
      </div>
    </form>
  );
}

function StepCardImpl({
  step,
  badge,
  readOnly,
  needsAttention,
  canEdit,
  onEdit,
  onDelete,
  onDelayChange,
  onClick,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [delayOpen, setDelayOpen] = useState(false);
  const rootRef = useRef(null);
  useOutsideClose(rootRef, menuOpen || delayOpen, () => {
    setMenuOpen(false);
    setDelayOpen(false);
  });

  const delayAmount = formatDelayAmount(step);
  const description = needsAttention ? "Action required" : STEP_META[step.type]?.description || "";
  const clickable = readOnly ? Boolean(onClick) : canEdit;

  const openBody = () => {
    if (readOnly) onClick?.(step);
    else if (canEdit) onEdit?.(step);
  };

  return (
    <div
      ref={rootRef}
      data-nopan
      className={`relative w-[290px] rounded-xl border bg-white shadow-[0_2px_10px_rgba(15,23,42,0.08)] ${
        needsAttention ? "border-red-500" : "border-gray-200"
      } ${menuOpen || delayOpen ? "z-30" : ""}`}
    >
      {typeof badge === "number" ? (
        <span className="absolute -top-2.5 -right-2.5 min-w-[24px] h-6 px-1.5 rounded-full bg-blue-500 text-white text-[11px] flex items-center justify-center font-semibold">
          {badge}
        </span>
      ) : null}

      <div className="flex items-center gap-2 px-3 py-1.5 border-b border-gray-200 bg-gray-50 rounded-t-xl text-[13px]">
        <ClockIcon size={16} className="text-gray-500" />
        {delayAmount ? (
          <span className="text-gray-600">
            Wait <span className="font-medium text-blue-600">{delayAmount}</span>, then
          </span>
        ) : (
          <span className="text-gray-400">No delay</span>
        )}
        {!readOnly ? (
          <button
            type="button"
            onClick={() => {
              setMenuOpen(false);
              setDelayOpen((v) => !v);
            }}
            title="Edit delay"
            className="ml-auto w-6 h-6 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 flex items-center justify-center"
          >
            <PencilIcon size={14} />
          </button>
        ) : null}
      </div>

      <div className="flex items-center gap-3 px-3 py-3">
        <button
          type="button"
          onClick={openBody}
          disabled={!clickable}
          className="flex items-center gap-3 flex-1 min-w-0 text-left disabled:cursor-default"
        >
          <span
            className={`w-10 h-10 rounded-lg border bg-white flex items-center justify-center shrink-0 ${
              needsAttention ? "border-gray-200 text-red-600" : "border-gray-200 text-blue-600"
            }`}
          >
            {needsAttention ? (
              <AlertTriangleIcon size={18} className="fill-red-600 stroke-white" />
            ) : (
              <StepTypeIcon type={step.type} size={18} />
            )}
          </span>
          <span className="min-w-0">
            <span
              className={`block text-sm font-medium truncate ${
                needsAttention ? "text-red-600" : "text-gray-900"
              }`}
            >
              {formatStepCanvasLabel(step)}
            </span>
            <span
              className={`block text-xs truncate ${needsAttention ? "text-red-500" : "text-gray-500"}`}
            >
              {description}
            </span>
          </span>
        </button>
        {!readOnly ? (
          <button
            type="button"
            onClick={() => {
              setDelayOpen(false);
              setMenuOpen((v) => !v);
            }}
            title="Step options"
            className="w-7 h-7 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 flex items-center justify-center shrink-0"
          >
            <MoreVerticalIcon size={16} />
          </button>
        ) : null}
      </div>

      {menuOpen ? (
        <div className="absolute right-2 top-full -mt-2 z-30 w-44 rounded-xl border border-gray-200 bg-white py-1 shadow-lg text-sm">
          {canEdit ? (
            <button
              type="button"
              onClick={() => {
                setMenuOpen(false);
                onEdit?.(step);
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-left text-gray-700 hover:bg-gray-50"
            >
              <PencilIcon size={14} /> Edit step
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => {
              setMenuOpen(false);
              setDelayOpen(true);
            }}
            className="w-full flex items-center gap-2 px-3 py-2 text-left text-gray-700 hover:bg-gray-50"
          >
            <ClockIcon size={14} /> Edit delay
          </button>
          <button
            type="button"
            onClick={() => {
              setMenuOpen(false);
              onDelete?.(step);
            }}
            className="w-full flex items-center gap-2 px-3 py-2 text-left text-red-600 hover:bg-red-50"
          >
            <TrashIcon size={14} /> Delete step
          </button>
        </div>
      ) : null}

      {delayOpen ? (
        <DelayEditor
          step={step}
          onClose={() => setDelayOpen(false)}
          onSave={(delay) => {
            setDelayOpen(false);
            onDelayChange?.(step, delay);
          }}
        />
      ) : null}
    </div>
  );
}

export const StepCard = memo(StepCardImpl);
